import React, { useEffect, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { useNavigate } from "react-router-dom";
import "./loginPage.css";

const loginRequest = {
  scopes: ["User.Read", "email", "profile", "openid"],
};

const LoginPage = () => {
  const { instance, accounts, inProgress } = useMsal();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Auto-login effect - checks for existing accounts on mount
  useEffect(() => {
    console.log("=== LoginPage useEffect triggered ===");
    console.log("inProgress:", inProgress);
    console.log("accounts:", accounts);
    console.log("accounts.length:", accounts.length);

    const attemptSilentLogin = async () => {
      // If authentication is already in progress, wait
      if (inProgress !== "none") {
        console.log("Authentication in progress, waiting...");
        return;
      }

      try {
        // Check if there are any cached accounts
        const currentAccounts = instance.getAllAccounts();
        console.log("currentAccounts from getAllAccounts():", currentAccounts);
        console.log("currentAccounts.length:", currentAccounts.length);

        if (currentAccounts.length > 0) {
          console.log("Found cached account:", currentAccounts[0]);
          console.log("Account username:", currentAccounts[0].username);
          console.log("Account name:", currentAccounts[0].name);

          // User has a cached account, attempt silent sign-in
          const silentRequest = {
            ...loginRequest,
            account: currentAccounts[0],
          };

          console.log("Attempting silent login with request:", silentRequest);

          try {
            const response = await instance.acquireTokenSilent(silentRequest);
            console.log("Silent login successful!");
            console.log("Auth response:", response);
            console.log(
              "Access token acquired:",
              response.accessToken ? "YES" : "NO",
            );
            console.log("Account info:", response.account);

            // Navigate to home page on successful silent login
            console.log("Navigating to /home...");
            navigate("/home", { replace: true });
          } catch (silentError) {
            console.error("Silent login failed:", silentError);
            console.error("Error name:", silentError.name);
            console.error("Error message:", silentError.message);
            console.error("Error stack:", silentError.stack);
            setIsLoading(false);
          }
        } else {
          console.log("No cached accounts found - user needs to login");
          setIsLoading(false);
        }
      } catch (err) {
        console.error("Auto-login error:", err);
        console.error("Error details:", {
          name: err.name,
          message: err.message,
          stack: err.stack,
        });
        setError(
          "An error occurred during auto-login. Please try logging in manually.",
        );
        setIsLoading(false);
      }
    };

    attemptSilentLogin();
  }, [instance, inProgress, navigate, accounts]);

  // Handle interactive login (when user clicks the button)
  const handleLogin = async () => {
    console.log("=== handleLogin clicked ===");
    setError(null);
    setIsLoading(true);

    try {
      console.log("Initiating loginRedirect with request:", loginRequest);
      await instance.loginRedirect(loginRequest);
      console.log("loginRedirect called - page should redirect now");
      // Note: loginRedirect will redirect the page, so code after this won't execute
    } catch (err) {
      console.error("Login error:", err);
      console.error("Error details:", {
        name: err.name,
        message: err.message,
        stack: err.stack,
      });
      setError("Login failed. Please try again or contact support.");
      setIsLoading(false);
    }
  };

  // Show loading spinner while checking for auto-login
  if (isLoading) {
    console.log("Rendering loading spinner...");
    return (
      <div className="login-container">
        <div className="login-card">
          <div className="login-header">
            <h1>RTLS Dashboard</h1>
            <p className="login-subtitle">PROD Environment</p>
          </div>
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>Checking authentication...</p>
          </div>
        </div>
      </div>
    );
  }

  console.log("Rendering login form...");
  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <h1>RTLS Dashboard</h1>
          <p className="login-subtitle">Real-Time Location System</p>
          <p className="environment-badge">PROD Environment</p>
        </div>

        <div className="login-body">
          <p className="login-description">
            Sign in with your Zekelman Industries work account to access the
            dashboard.
          </p>

          {error && (
            <div className="error-message">
              <span className="error-icon">!</span>
              <span>{error}</span>
            </div>
          )}

          <button
            onClick={handleLogin}
            className="login-button"
            disabled={isLoading}
          >
            <svg
              className="microsoft-icon"
              viewBox="0 0 23 23"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M0 0h11v11H0z" fill="#f25022" />
              <path d="M12 0h11v11H12z" fill="#00a4ef" />
              <path d="M0 12h11v11H0z" fill="#7fba00" />
              <path d="M12 12h11v11H12z" fill="#ffb900" />
            </svg>
            Sign in with Microsoft
          </button>

          <div className="login-footer">
            <p className="security-note">
              Your credentials are secured by Microsoft Entra ID
            </p>
          </div>
        </div>
      </div>

      <footer className="page-footer">
        <p>
          &copy; {new Date().getFullYear()} Zekelman Industries. All rights
          reserved.
        </p>
      </footer>
    </div>
  );
};

export default LoginPage;
